/**
 * Begriffe für den Editor: das Glossar des Mandanten als Liste neben dem Text, filterbar nach Namen.
 * Ziehen in den Text (oder Klick bei geöffnetem Editor) setzt den Begriff als Verweis auf seine
 * Definition: in dieser Richtlinie auf den Definitionsabsatz, sonst auf den Abschnitt der Richtlinie,
 * die ihn definiert; nur im Glossar festgelegt: als Text.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookMarked, GripVertical } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { PUBLIC_GUIDELINES_URL } from "./guidelinesPath";
import { useGuidelinesAPI, type GlossaryEntry } from "./GuidelinesAPI";
import { TERM_DRAG_TYPE, type TermInsert } from "./plate/PlateMarkdownEditor.types";

/** So viele Zeichen der Definition in der Liste; der Rest beim Überfahren. */
const PREVIEW = 90;

/** Wohin der Verweis zeigt: Definition in dieser Richtlinie, sonst das erste Vorkommen anderswo. */
export const termTarget = (entry: GlossaryEntry, documentId: string): { insert: TermInsert; where: string | null } => {
  const here = entry.occurrences.find((o) => o.documentId === documentId && o.element);
  if (here) return { insert: { text: entry.term, href: `#${here.element}` }, where: here.number };
  const other = entry.occurrences.find((o) => o.documentId);
  if (other)
    return {
      insert: { text: entry.term, href: `${PUBLIC_GUIDELINES_URL}/${other.documentId}${other.number ? `?section=${encodeURIComponent(other.number)}` : ""}` },
      where: `${other.guideline} ${other.number ?? ""}`.trim(),
    };
  return { insert: { text: entry.term, href: null }, where: null };
};

export function TermsPanel(props: { customer: string; documentId: string; canInsert: boolean; onInsert: (term: TermInsert) => void }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [entries, setEntries] = useState<GlossaryEntry[] | null>(null);
  const [filter, setFilter] = useState("");
  const [onlyDefined, setOnlyDefined] = useState(false);

  useEffect(() => {
    api
      .getGlossary()
      .then((v) => setEntries([...v.entries].sort((a, b) => a.term.localeCompare(b.term, "de"))))
      .catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customer]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (entries ?? []).filter((e) => (!onlyDefined || !!e.glossary) && (!q || e.term.toLowerCase().includes(q) || (e.occurrences[0]?.qualifier ?? "").toLowerCase().includes(q)));
  }, [entries, filter, onlyDefined]);

  if (!entries) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="terms-panel">
      <Input placeholder={t("guidelinesPage.terms.filter")} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="terms-filter" />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={onlyDefined} onChange={(e) => setOnlyDefined(e.target.checked)} data-testid="terms-only-defined" />
        {t("guidelinesPage.terms.onlyDefined")}
        <span className="tabular-nums text-muted-foreground">({entries.filter((e) => e.glossary).length})</span>
      </label>
      <p className="text-xs text-muted-foreground">{props.canInsert ? t("guidelinesPage.terms.hintEditing") : t("guidelinesPage.terms.hintReading")}</p>
      <ul className="flex flex-col gap-1">
        {shown.map((e) => {
          const definition = e.glossary?.definition ?? e.reference ?? "";
          const { insert, where } = termTarget(e, props.documentId);
          const preview = definition.length > PREVIEW ? `${definition.slice(0, PREVIEW).replace(/\s+\S*$/, "")} …` : definition;
          return (
            <li key={e.key}>
              <div
                role="button"
                tabIndex={0}
                draggable
                data-term={e.key}
                title={[definition, where ? `— ${where}` : ""].filter(Boolean).join(" ")}
                onDragStart={(ev) => {
                  ev.dataTransfer.setData(TERM_DRAG_TYPE, JSON.stringify(insert));
                  ev.dataTransfer.setData("text/plain", insert.text);
                  ev.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => (props.canInsert ? props.onInsert(insert) : toast.info(t("guidelinesPage.terms.openEditor")))}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" && props.canInsert) props.onInsert(insert);
                }}
                className="group flex cursor-grab gap-2 rounded-md border bg-card p-2 text-sm hover:bg-accent active:cursor-grabbing"
              >
                <GripVertical size={14} className="mt-0.5 shrink-0 text-muted-foreground opacity-50 group-hover:opacity-100" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium">{e.term}</span>
                    {e.occurrences[0]?.qualifier ? <span className="text-xs text-muted-foreground">({e.occurrences[0].qualifier})</span> : null}
                    {e.kind === "abbreviation" ? <Badge variant="outline" className="px-1 py-0 text-[10px] font-normal">{t("guidelinesPage.glossary.abbreviation")}</Badge> : null}
                    {e.glossary ? <BookMarked size={12} className="text-muted-foreground" aria-label={t("guidelinesPage.glossary.inGlossary")} /> : null}
                  </div>
                  <div className="text-xs text-muted-foreground">{preview}</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {!shown.length ? <p className="text-sm text-muted-foreground">{t("guidelinesPage.glossary.empty")}</p> : null}
    </div>
  );
}
